"use client";

import { useEffect, type RefObject } from "react";
import { useFocusTrap } from "@/hooks/use-focus-trap";

/**
 * Le comportement clavier commun à toutes les modales : piège de focus,
 * `Escape` pour fermer, focus posé sur le conteneur à l'ouverture.
 *
 * Extrait de `ModalTemplate`, qui le portait en propre. L'écran de compte a
 * besoin du même comportement mais pas du même habillage — barre de titre,
 * séparateur, titre en dégradé — et dupliquer un piège de focus est la
 * meilleure façon d'en avoir deux versions dont une seule est correcte.
 *
 * `onClose` est lu à chaque frappe plutôt que capturé : une modale qui change
 * d'étape change souvent de gestionnaire de fermeture (revenir en arrière
 * plutôt que fermer), et le rattacher au clavier à chaque rendu est plus sûr
 * que de compter sur la stabilité de la référence.
 */
export function useModalShell(
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void,
): void {
  useFocusTrap(ref, open);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Focus à l'ouverture, le piège prend le relais dès la première tabulation.
  useEffect(() => {
    if (open && ref.current) {
      ref.current.focus();
    }
  }, [open, ref]);
}
